import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { TreeItem, treeItemClasses, TreeItemProps } from '@mui/x-tree-view';

import 'style/constants.scss';

import Constants from 'style/constants';

import styles from './organisation-unit-tree-item.module.scss';

type Props = TreeItemProps & {
  labelText: string;
  isRoot: boolean;
  startingIcons?: React.JSX.Element;
  endingIcons?: React.JSX.Element;
  disableSelection: boolean;
  showSelectionInViewMode?: boolean;
  highlightColor?: string;
  highlightedHoverColor?: string;
  showExpandIndicator?: boolean;
};

type StyledTreeItemProps = {
  disableSelection: boolean;
  showSelectionInViewMode: boolean;
  highlightColor?: string;
  highlightedHoverColor?: string;
};

const getSelectionBackgroundColor = (
  isHover: boolean,
  disableSelection: boolean,
  showSelectionInViewMode: boolean,
  highlightColor?: string,
  highlightedHoverColor?: string,
): string => {
  if (disableSelection && !showSelectionInViewMode) {
    return 'transparent';
  }
  if (showSelectionInViewMode) {
    return '#E6E1E6';
  }
  return isHover ? (highlightedHoverColor ?? Constants.Colors.secondaryContainer) : (highlightColor ?? Constants.Colors.secondaryContainer);
};

const OrganisationUnitTreeItemRoot = styled(TreeItem, {
  shouldForwardProp: (prop) =>
    prop !== 'disableSelection' && prop !== 'showSelectionInViewMode' && prop !== 'highlightColor' && prop !== 'highlightedHoverColor',
})<StyledTreeItemProps>(({ disableSelection, showSelectionInViewMode, highlightColor, highlightedHoverColor }) => ({
  [`& .${treeItemClasses.content}`]: {
    marginTop: '20px',
    margin: '0px 4px',
    padding: '0px 4px',
    height: '40px',
    width: 'auto',
    borderRadius: '28px',
    minWidth: '350px',
    gap: 0,
    cursor: disableSelection ? 'not-allowed' : 'pointer',
    '&:hover': {
      backgroundColor: disableSelection ? 'transparent' : alpha(Constants.Colors.secondaryContainer, 0.16),
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
    },
    '&.Mui-selected, &.Mui-selected.Mui-focused': {
      backgroundColor: getSelectionBackgroundColor(false, disableSelection, showSelectionInViewMode, highlightColor, highlightedHoverColor),
      borderRadius: showSelectionInViewMode ? '28px' : undefined,
    },
    '&.Mui-selected:hover': {
      backgroundColor: getSelectionBackgroundColor(true, disableSelection, showSelectionInViewMode, highlightColor, highlightedHoverColor),
      borderRadius: showSelectionInViewMode ? '28px' : undefined,
    },
    '&.MuiTreeItem-content': {
      cursor: 'pointer',
    },
    [`& .${treeItemClasses.iconContainer}`]: {
      height: '32px',
      width: '32px',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0',
      '&:empty': {
        display: 'none',
      },
      '&:hover': {
        backgroundColor: alpha('#241a00', 0.16),
        borderRadius: '24px',
      },
    },
    '& .MuiTreeItem-label': {
      paddingLeft: '4px',
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 20,
    paddingLeft: 4,
    borderLeft: `1px solid ${alpha('#71787D29', 0.16)}`,
    marginBottom: '8px',
  },
}));

const OrganisationUnitTreeItem = ({
  labelText,
  isRoot,
  startingIcons,
  endingIcons,
  disableSelection,
  showSelectionInViewMode,
  highlightColor,
  highlightedHoverColor,
  showExpandIndicator = true,
  ...other
}: Readonly<Props>): React.JSX.Element => {
  const CustomExpandIcon = () => {
    return showExpandIndicator ? <ArrowRightIcon className={styles.dropdownIcon} /> : null;
  };

  const CustomCollapseIcon = () => {
    return showExpandIndicator ? <ArrowDropDownIcon className={styles.dropdownIcon} /> : null;
  };

  return (
    <>
      {isRoot && <Divider />}
      <OrganisationUnitTreeItemRoot
        sx={{ paddingTop: '8px' }}
        disableSelection={disableSelection}
        showSelectionInViewMode={showSelectionInViewMode ?? false}
        highlightColor={highlightColor}
        highlightedHoverColor={highlightedHoverColor}
        slots={{
          expandIcon: CustomExpandIcon,
          collapseIcon: CustomCollapseIcon,
        }}
        label={
          <Box
            className={styles.treeItemContainer}
            sx={{
              cursor: disableSelection ? 'not-allowed' : 'pointer',
              pointerEvents: disableSelection ? 'none' : 'auto',
              opacity: disableSelection ? 0.5 : 1,
              filter: disableSelection ? 'grayscale(100%)' : 'none',
            }}
          >
            <div className={styles.startingIconsContainer}>{startingIcons}</div>
            <Typography
              sx={{ paddingLeft: '8px' }}
              className={styles.ellipsis}
              variant="subtitle1"
              data-testid={`OrganisationUnitTreeItemLabel-${labelText}`}
            >
              {labelText}
            </Typography>
            <div className={styles.endingIconsContainer}>{endingIcons}</div>
          </Box>
        }
        {...other}
      />
    </>
  );
};

export default OrganisationUnitTreeItem;
